import { useRouter } from "@/lib/nav";
import { useState } from "react";

import { UNSAVED_LEAVE_CONFIRM } from "@/components/save-bar";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Textarea } from "@buildpy/ui/textarea";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import { formatNumber } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import { ANONYMOUS_AUTHOR_LABEL, isAnonymousLabel } from "@buildpy/core/modules/review/rules";
import { saveCustomerReviewByLink } from "@/api/actions/review-share";
import { MAX_REVIEW_BODY, type CustomerReview } from "@buildpy/core/modules/review/types";

interface Props {
  token: string;
  // 이름을 가린 표기(「김○○ 고객」) — 서버가 같은 규칙(reviewAuthorLabelOf)으로 만든다.
  namedLabel: string;
  review: Pick<CustomerReview, "body" | "author_label" | "consent_public"> | null;
}

const FAILED = "저장하지 못했습니다. 잠시 뒤 다시 저장해 주세요";
const TEST_ID = "customer-review-form";

// 서버 검사와 같은 코드포인트 기준으로 센다.
const lengthOf = (value: string): number => Array.from(value).length;

// 고객 후기 한 편 — 쓰기·고치기·공개 동의 철회가 모두 한 번의 저장이다.
const ReviewForm = ({ token, namedLabel, review }: Props) => {
  const router = useRouter();
  const [body, setBody] = useState(review?.body ?? "");
  const [anonymous, setAnonymous] = useState(review ? isAnonymousLabel(review.author_label) : false);
  const [consent, setConsent] = useState(review?.consent_public ?? false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pending, setPending] = useState(false);
  const [locked, setLocked] = useState(false);
  // 마지막으로 저장한 값 — 그와 다르면 떠날 때(다른 탭·뒤로·새로고침) 묻는다.
  const [savedForm, setSavedForm] = useState(() => ({ body, anonymous, consent }));
  const dirty = body !== savedForm.body || anonymous !== savedForm.anonymous || consent !== savedForm.consent;
  // 잠겼으면(더 저장할 수 없음) 떠날 때 묻지 않는다.
  const guard = dirty && !locked;
  useLeaveGuard({ unload: guard, links: guard, back: guard, message: UNSAVED_LEAVE_CONFIRM });
  const length = lengthOf(body);
  const blank = body.trim() === "";

  const submit = async () => {
    setPending(true);
    setError(null);
    setSaved(false);
    const sent = { body, anonymous, consent };
    try {
      const result = await saveCustomerReviewByLink({ token, body, anonymous, consent_public: consent });
      if (!result.ok) {
        setError(result.message);
        if (result.locked) setLocked(true);
        if (result.reverify || result.not_open) router.refresh();
        return;
      }
      setSaved(true);
      setSavedForm(sent);
      router.refresh();
    } catch (e) {
      if (settleRedirect(e)) return;
      setError(FAILED);
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-y-3"
      data-testid={TEST_ID}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}>
      <Textarea
        aria-label="후기 내용"
        className="min-h-[120px]"
        data-testid={`${TEST_ID}-body`}
        disabled={pending || locked}
        placeholder="공사는 어떠셨나요? 좋았던 점, 아쉬웠던 점을 자유롭게 적어 주세요"
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      <span className={length > MAX_REVIEW_BODY ? "self-end text-xs text-destructive" : "self-end text-xs text-muted-foreground"}>
        {formatNumber(length)} / {formatNumber(MAX_REVIEW_BODY)}
      </span>

      <label className="flex items-start gap-x-2 text-sm">
        <Checkbox
          checked={anonymous}
          className="mt-0.5"
          data-testid={`${TEST_ID}-anonymous`}
          disabled={pending || locked}
          onCheckedChange={(checked) => setAnonymous(checked === true)}
        />
        <span>
          이름 대신 「{ANONYMOUS_AUTHOR_LABEL}」으로 표기합니다
          <span className="block text-xs text-muted-foreground" data-testid={`${TEST_ID}-label`}>
            표기: {anonymous ? ANONYMOUS_AUTHOR_LABEL : namedLabel}
          </span>
        </span>
      </label>

      <label className="flex items-start gap-x-2 text-sm">
        <Checkbox
          checked={consent}
          className="mt-0.5"
          data-testid={`${TEST_ID}-consent`}
          disabled={pending || locked}
          onCheckedChange={(checked) => setConsent(checked === true)}
        />
        <span>
          시공사 홈페이지·시공사례에 이 후기를 공개하는 데 동의합니다
          <span className="block text-xs text-muted-foreground">
            동의하지 않으면 담당 시공사만 봅니다. 동의는 언제든 끄고 다시 저장하면 바로 거둬집니다.
          </span>
        </span>
      </label>

      <Button data-testid={`${TEST_ID}-submit`} disabled={pending || locked || blank || length > MAX_REVIEW_BODY} type="submit">
        {pending ? "저장하는 중" : review ? "후기 고치기" : "후기 남기기"}
      </Button>
      {saved && !error && (
        <p className="text-sm text-muted-foreground" data-testid={`${TEST_ID}-saved`} role="status">
          저장했습니다. 감사합니다.
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid={`${TEST_ID}-error`} role="alert">
          {error}
        </p>
      )}
    </form>
  );
};

export default ReviewForm;
